import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  TrendingDown, TrendingUp, Minus, AlertCircle, Sparkles, RefreshCw,
  CheckCircle2, ChevronRight,
} from "lucide-react";
import ItemIntelligencePanel from "./ItemIntelligencePanel";

const STATUS_STYLES = {
  benchmark_cheaper: { cls: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)] border-[hsl(6_62%_45%)]/30", icon: TrendingDown, label: "Cheaper elsewhere" },
  supplier_better: { cls: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))] border-[hsl(var(--forest))]/30", icon: TrendingUp, label: "Supplier better" },
  same: { cls: "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)] border-[hsl(35_75%_38%)]/30", icon: Minus, label: "Same price" },
  no_benchmark: { cls: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] border-[hsl(var(--rule))]", icon: AlertCircle, label: "No benchmark" },
};

const RECO_STYLES = {
  switch: "bg-[hsl(6_60%_97%)] border-[hsl(6_62%_45%)]/40 text-[hsl(6_62%_45%)]",
  keep: "bg-[hsl(148_40%_95%)] border-[hsl(var(--forest))]/40 text-[hsl(var(--forest))]",
  review: "bg-[hsl(38_90%_95%)] border-[hsl(35_75%_38%)]/40 text-[hsl(35_75%_38%)]",
  empty: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
  no_data: "bg-[hsl(var(--bg-2))] border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const inr2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function POBenchmarkPanel({ poId, editable = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [filter, setFilter] = useState("all"); // all | cheaper | supplier | missing
  const [itemPanel, setItemPanel] = useState(null);

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/staff/procurement/po-benchmarks/${poId}`, { params: { live: opts.live ? "true" : "false" } });
      setData(d);
      setLive(!!d.live);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [poId]);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      await api.post(`/staff/procurement/po-benchmarks/${poId}/snapshot`);
      await load();
    } catch (e) { /* noop */ }
    finally { setRebuilding(false); }
  };

  if (loading) return <div className="mt-6 p-4 text-xs text-[hsl(var(--ink-soft))]">Loading benchmark comparison…</div>;
  if (!data || !data.summary) return null;

  const s = data.summary;
  const rec = data.recommendation || {};
  const rows = (data.rows || []).filter((r) => {
    if (filter === "all") return true;
    if (filter === "cheaper") return r.status === "benchmark_cheaper";
    if (filter === "supplier") return r.status === "supplier_better";
    if (filter === "missing") return r.status === "no_benchmark";
    return true;
  });

  return (
    <section data-testid={`po-benchmarks-${poId}`} className="mt-8 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
      <div className="flex items-center justify-between p-3 border-b border-[hsl(var(--rule))]">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><TrendingDown size={12} /> Procurement Intelligence</div>
          <div className="text-xs text-[hsl(var(--ink-soft))] mt-0.5">
            {live ? "Live comparison" : "Snapshot at PO creation"} · {s.items_total} line{s.items_total === 1 ? "" : "s"} · {s.coverage_pct}% benchmark coverage
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="po-benchmark-toggle-live"
            onClick={() => load({ live: !live })}
            className="text-[10px] px-2 py-1 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))]"
          >
            {live ? "Show snapshot" : "Show live"}
          </button>
          {editable && (
            <button
              data-testid="po-benchmark-rebuild"
              onClick={rebuild}
              disabled={rebuilding}
              className="text-[10px] px-2 py-1 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))] inline-flex items-center gap-1"
            >
              <RefreshCw size={10} className={rebuilding ? "animate-spin" : ""} /> Re-snap
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--rule))]">
        <SummaryCard label="PO Value" value={inr(s.po_value)} tone="ink" testid="po-bench-po-value" />
        <SummaryCard label="Benchmark equivalent" value={inr(s.benchmark_equivalent_value)} tone="ink" testid="po-bench-eq-value" />
        <SummaryCard
          label="Potential savings"
          value={inr(s.potential_savings)}
          sub={`${s.savings_pct}%`}
          tone={s.potential_savings > 0 ? "danger" : "forest"}
          testid="po-bench-savings"
        />
        <SummaryCard
          label="Coverage"
          value={`${s.coverage_pct}%`}
          sub={`${s.items_total - s.items_missing_benchmark}/${s.items_total} items`}
          tone={s.coverage_pct === 100 ? "forest" : s.coverage_pct >= 50 ? "brass" : "danger"}
          testid="po-bench-coverage"
        />
      </div>

      {/* Recommendation strip */}
      {rec.message && (
        <div data-testid="po-bench-recommendation" className={`p-3 border-t border-[hsl(var(--rule))] text-xs flex items-center gap-2 ${RECO_STYLES[rec.kind] || RECO_STYLES.empty}`}>
          {rec.kind === "switch" ? <TrendingDown size={14} /> : rec.kind === "keep" ? <CheckCircle2 size={14} /> : rec.kind === "review" ? <Sparkles size={14} /> : <AlertCircle size={14} />}
          <span>{rec.message}</span>
          {rec.suggested_vendor && <span className="ml-auto font-semibold">→ {rec.suggested_vendor}</span>}
        </div>
      )}

      {/* Chips: per-vendor totals */}
      {(s.per_vendor || []).length > 0 && (
        <div className="p-3 border-t border-[hsl(var(--rule))] flex flex-wrap gap-1.5">
          {s.per_vendor.map((pv) => (
            <div key={pv.vendor_id} className="text-[10px] px-2 py-1 bg-[hsl(var(--bg))] border border-[hsl(var(--rule))]" data-testid={`po-bench-vendor-${pv.vendor_id}`}>
              <span className="text-[hsl(var(--ink-soft))]">{pv.vendor_name}:</span>{" "}
              <span className="font-mono">{pv.items_covered ? inr(pv.total_value) : "—"}</span>
              {pv.items_covered > 0 && <span className="text-[hsl(var(--ink-soft))]"> ({pv.items_covered}/{s.items_total})</span>}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="px-3 pt-3 flex flex-wrap gap-1">
        {[
          { key: "all", label: `All (${s.items_total})` },
          { key: "cheaper", label: `Cheaper elsewhere (${s.items_cheaper_elsewhere})` },
          { key: "supplier", label: `Supplier better (${s.items_supplier_better})` },
          { key: "missing", label: `Missing (${s.items_missing_benchmark})` },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`po-bench-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-[10px] px-2 py-1 border rounded ${filter === f.key ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))]/10 text-[hsl(var(--forest))]" : "border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Line-by-line comparison */}
      <div className="p-3">
        <div className="border border-[hsl(var(--rule))] overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[hsl(var(--bg))] text-[hsl(var(--ink-soft))] uppercase tracking-wider">
              <tr>
                <th className="text-left p-2">Item</th>
                <th className="text-right p-2">Qty</th>
                <th className="text-right p-2">Supplier</th>
                <th className="text-right p-2">Best benchmark</th>
                <th className="text-right p-2">Diff</th>
                <th className="text-right p-2">Savings</th>
                <th className="text-center p-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--rule))]">
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-[hsl(var(--ink-soft))]">No lines in this filter.</td></tr>
              ) : rows.map((r, i) => {
                const st = STATUS_STYLES[r.status] || STATUS_STYLES.no_benchmark;
                const StIcon = st.icon;
                return (
                  <tr key={i} data-testid={`po-bench-row-${i}`}>
                    <td className="p-2">
                      <button
                        data-testid={`po-bench-item-detail-${i}`}
                        onClick={() => setItemPanel({ item_name: r.item_name, item_id: r.item_id, unit: r.unit })}
                        className="font-medium hover:underline text-left"
                      >
                        {r.item_name}
                      </button>
                      <div className="text-[10px] text-[hsl(var(--ink-soft))]">{r.unit}</div>
                    </td>
                    <td className="p-2 text-right font-mono">{r.qty_ordered}</td>
                    <td className="p-2 text-right font-mono">{inr2(r.supplier_rate)}</td>
                    <td className="p-2 text-right font-mono">
                      {r.best ? (
                        <>
                          <div>{inr2(r.best.price)}</div>
                          <div className="text-[9px] text-[hsl(var(--ink-soft))]">{r.best.vendor_name}</div>
                        </>
                      ) : <span className="text-[hsl(var(--ink-soft))]">—</span>}
                    </td>
                    <td className={`p-2 text-right font-mono ${r.diff > 0 ? "text-[hsl(6_62%_45%)]" : r.diff < 0 ? "text-[hsl(var(--forest))]" : ""}`}>
                      {r.best ? (r.diff > 0 ? "+" : "") + inr2(r.diff) : "—"}
                    </td>
                    <td className={`p-2 text-right font-mono ${r.savings > 0 ? "text-[hsl(6_62%_45%)] font-semibold" : r.savings < 0 ? "text-[hsl(var(--forest))]" : "text-[hsl(var(--ink-soft))]"}`}>
                      {r.best ? (r.savings > 0 ? inr(r.savings) : r.savings < 0 ? `(${inr(Math.abs(r.savings))})` : "—") : "—"}
                      {r.best && r.savings_pct !== 0 && (
                        <div className="text-[9px] font-normal opacity-70">{r.savings_pct > 0 ? "+" : ""}{r.savings_pct}%</div>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] whitespace-nowrap ${st.cls}`}>
                        <StIcon size={10} /> {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[10px] text-[hsl(var(--ink-soft))]">
          Diff = Supplier rate − Best benchmark rate. Positive diff (red) → benchmark cheaper by that amount.
          Savings = Diff × Qty. Best benchmark = cheapest available across all active vendors.
        </div>
      </div>
      {itemPanel && (
        <ItemIntelligencePanel
          itemName={itemPanel.item_name}
          itemId={itemPanel.item_id}
          unit={itemPanel.unit}
          onClose={() => setItemPanel(null)}
        />
      )}
    </section>
  );
}

function SummaryCard({ label, value, sub, tone, testid }) {
  const toneCls = tone === "danger" ? "text-[hsl(6_62%_45%)]"
    : tone === "forest" ? "text-[hsl(var(--forest))]"
    : tone === "brass" ? "text-[hsl(var(--brass-2))]"
    : "text-[hsl(var(--ink))]";
  return (
    <div data-testid={testid} className="bg-[hsl(var(--bg))] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`mt-1 display text-xl ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-[hsl(var(--ink-soft))] mt-0.5">{sub}</div>}
    </div>
  );
}
